import {
  CirclePlay,
  Coins,
  Eye,
  FileLock2,
  FileText,
  Image,
  MessageSquare,
  Radio,
  RefreshCw,
  Share2,
  ShoppingCart,
  Sparkles,
  ThumbsUp,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

/**
 * Figma Engagement palette. Content types keep their own hue across the donut,
 * the by-type table and the ranking lists; interaction metrics keep the hue
 * used by the Engagement-by-Content-Type header legend.
 */
export const ENGAGEMENT_COLORS: Record<string, string> = {
  FREE: '#3b9dff',
  EXCLUSIVE: '#f4485c',
  FLICK: '#a855f7',
  PICTURE: '#22c55e',
  STORY: '#f59e0b',
  LIVE: '#94a3b8',
  VIEWS: '#3b9dff',
  LIKES: '#f472b6',
  COMMENTS: '#a78bfa',
  SHARES: '#22c55e',
  TIPS: '#3b9dff',
  SUBSCRIPTIONS: '#a855f7',
  PURCHASES: '#f4485c',
};

export function engagementColor(key: string | undefined | null, fallback = '#7a7a78'): string {
  if (!key) return fallback;
  return ENGAGEMENT_COLORS[key] ?? ENGAGEMENT_COLORS[key.toUpperCase()] ?? fallback;
}

export type EngagementIconMeta = {
  icon: LucideIcon;
  color: string;
};

const CONTENT_TYPE_ICONS: Record<string, LucideIcon> = {
  FREE: FileText,
  EXCLUSIVE: FileLock2,
  FLICK: CirclePlay,
  PICTURE: Image,
  STORY: Sparkles,
  LIVE: Radio,
};

/** Icon + hue for a content type key (FREE, EXCLUSIVE, FLICK, PICTURE, STORY, LIVE). */
export function contentTypeMeta(type: string | undefined | null): EngagementIconMeta {
  const key = (type ?? '').toUpperCase();
  return {
    icon: CONTENT_TYPE_ICONS[key] ?? FileText,
    color: engagementColor(key),
  };
}

/** Short labels for the by-type table, which is too narrow for the full Confluence label. */
export const CONTENT_TYPE_SHORT_LABELS: Record<string, string> = {
  FREE: 'Posts-free',
  EXCLUSIVE: 'Exclusive',
  FLICK: 'Flicks',
  PICTURE: 'Pictures',
  STORY: 'Stories',
  LIVE: 'Live',
};

export function contentTypeShortLabel(type: string, fallback: string): string {
  return CONTENT_TYPE_SHORT_LABELS[type.toUpperCase()] ?? fallback;
}

/** Column order and styling for Engagement by Content Type. */
export const ENGAGEMENT_METRIC_COLUMNS = [
  { key: 'views', legend: 'VIEWS', label: 'Views', icon: Eye },
  { key: 'likes', legend: 'LIKES', label: 'Likes', icon: ThumbsUp },
  { key: 'comments', legend: 'COMMENTS', label: 'Comments', icon: MessageSquare },
  { key: 'shares', legend: 'SHARES', label: 'Shares', icon: Share2 },
] as const;

export type EngagementMetricColumn = (typeof ENGAGEMENT_METRIC_COLUMNS)[number];

export const ENGAGEMENT_RATE_ICON = TrendingUp;

const KPI_ICONS: Record<string, LucideIcon> = {
  TOTAL_CONTENT: FileText,
  TOTAL_VIEWS: Eye,
  TIPS: Coins,
  SUBSCRIPTIONS: RefreshCw,
  PURCHASES: ShoppingCart,
};

const KPI_COLORS: Record<string, string> = {
  TOTAL_CONTENT: '#94a3b8',
  TOTAL_VIEWS: '#94a3b8',
  TIPS: '#3b9dff',
  SUBSCRIPTIONS: '#a855f7',
  PURCHASES: '#f4485c',
};

export function kpiMeta(key: string | undefined): EngagementIconMeta {
  const id = (key ?? '').toUpperCase();
  return {
    icon: KPI_ICONS[id] ?? FileText,
    color: KPI_COLORS[id] ?? '#94a3b8',
  };
}

export const engagementRangeOptions = [
  { value: 'all', label: 'All the time' },
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 365 days' },
] as const;

const COUNTRY_LABELS: Record<string, string> = {
  AU: 'Australia',
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  NZ: 'New Zealand',
};

export function engagementCountryLabel(code: string): string {
  const upper = code.toUpperCase();
  if (COUNTRY_LABELS[upper]) return COUNTRY_LABELS[upper];
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(upper);
    if (name && name !== upper) return name;
  } catch {
    // Fall through to the ISO code when Intl has no English name.
  }
  return upper;
}

/**
 * Section “i” hover copy when Event Location is applied (TM-3661).
 * Keep business-facing — no PostHog / GeoIP / Connect jargon in the UI string.
 */
export const ENGAGEMENT_LOCATION_HELP = {
  audience:
    'When a location is selected, this is based on where the audience interacted.',
  connectCountry:
    "When a location is selected, this is based on the creator's registered payment country — not where the audience interacted. State selection uses country only.",
  referrals:
    'When a location is selected, this is based on where people signed up (registration location).',
  topPerforming:
    'When a location is selected, this ranks content by views from audience activity in that location.',
  monetisation:
    "When a location is selected, tips, subscriptions and purchases use the creator's registered payment country — not where the audience interacted (state uses country only).",
} as const;

/** Pass-through of API location codes (ISO country and `AU:VIC` state keys). */
export function engagementLocationCodes(availableCodes: readonly string[] = []): string[] {
  return [
    ...new Set(availableCodes.map((code) => code.trim().toUpperCase()).filter(Boolean)),
  ].filter((code) => code !== 'UNASSIGNED' && !isLocalhostGeoJunk(code));
}

/** MaxMind maps loopback to Sweden (`SE` / `SE:S` / `SE:E`) — never show in the picker. */
function isLocalhostGeoJunk(code: string): boolean {
  if (code === 'SE' || code === 'SE:S' || code === 'SE:E') return true;
  return false;
}
